import React, { useEffect, useState } from 'react'
import { dishesApi } from '../lib/api.js'

/**
 * DishLibraryPicker — bottom sheet to add a meal to a slot.
 * Two tabs:
 *   1. "Mes plats" — saved dishes + recents inferred from dish_logs
 *   2. "Texte libre" — free-form input
 *
 * Props:
 *   slotLabel: string (e.g. "Mardi — Dîner")
 *   onSelect:  (mealName, options?) => Promise|void
 *   onClose:   () => void
 */
export function DishLibraryPicker({ slotLabel, onSelect, onClose }) {
  const [tab, setTab] = useState('library')
  const [dishes, setDishes] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [freeText, setFreeText] = useState('')
  const [adding, setAdding] = useState(null)

  useEffect(() => {
    let cancelled = false
    dishesApi
      .list(50)
      .then((rows) => {
        if (!cancelled) setDishes(Array.isArray(rows) ? rows : [])
      })
      .catch(() => !cancelled && setDishes([]))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = query.trim()
    ? dishes.filter((d) => d.name.toLowerCase().includes(query.toLowerCase()))
    : dishes

  async function handlePick(dish) {
    setAdding(dish.name)
    try {
      await onSelect(dish.name, { prepTime: dish.prep_time })
    } finally {
      setAdding(null)
    }
  }

  async function handleFreeText() {
    const v = freeText.trim()
    if (!v) return
    setAdding(v)
    try {
      // Save to library AND add to slot
      await dishesApi.save({ name: v }).catch(() => {})
      await onSelect(v)
    } finally {
      setAdding(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50">
      <div className="bg-white rounded-t-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <p className="font-semibold text-gray-800">{slotLabel}</p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Fermer">
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 flex gap-1 border-b border-gray-100">
          <TabButton active={tab === 'library'} onClick={() => setTab('library')}>
            🍽️ Mes plats
          </TabButton>
          <TabButton active={tab === 'free'} onClick={() => setTab('free')}>
            ✏️ Texte libre
          </TabButton>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {tab === 'library' && (
            <>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un plat…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              {loading && <p className="text-xs text-gray-400 text-center py-6">Chargement…</p>}
              {!loading && filtered.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-400 mb-3">
                    {query
                      ? 'Aucun plat trouvé.'
                      : "Aucun plat dans votre bibliothèque pour l'instant."}
                  </p>
                  <button
                    onClick={() => {
                      setFreeText(query)
                      setTab('free')
                    }}
                    className="text-xs text-emerald-600 underline hover:no-underline"
                  >
                    Ajouter "{query || 'un plat'}" en texte libre
                  </button>
                </div>
              )}
              <ul className="space-y-1.5">
                {filtered.map((d) => (
                  <li key={d.id ?? `inferred-${d.name}`}>
                    <button
                      onClick={() => handlePick(d)}
                      disabled={adding === d.name}
                      className="w-full text-left flex items-center gap-3 border border-gray-100 rounded-xl px-3 py-2.5 hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors disabled:opacity-50"
                    >
                      <span className="text-xl flex-shrink-0">🍽️</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                        <p className="text-xs text-gray-400">
                          {d.use_count > 0 ? `${d.use_count}× cuisiné` : 'Nouveau'}
                          {d.prep_time && ` · ${d.prep_time} min`}
                          {d.inferred && ' · récent'}
                        </p>
                      </div>
                      <span className="text-emerald-500 text-sm font-semibold flex-shrink-0">+</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {tab === 'free' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Le plat sera ajouté à votre bibliothèque pour réutilisation.
              </p>
              <input
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Nom du plat…"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleFreeText()}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <button
                onClick={handleFreeText}
                disabled={!freeText.trim() || adding === freeText.trim()}
                className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {adding === freeText.trim() ? '…' : 'Ajouter au planner'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-emerald-500 text-emerald-700'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}
